/**
 * AER ROMÂNIA — Pagina de Analiză
 * Entry point separat — încarcă cuburile CAMS, generează text prognoză,
 * populează tabelul de orașe, carduri poluanți, mini-hartă.
 */
import { loadCube, loadStoreMetadata, timesToDates, extractTimeSeries, nearestIndex } from './data.js';
import { CITIES, addCustomCity } from './cities.js';
import { sampleCityValue } from './cityData.js';
import {
  getAqiCategory, AQI_CATEGORIES, THRESHOLDS,
  LABELS, UNITS, valueToColor, computeEAQI, eaqiLevelToColor
} from './colormaps.js';
import { generateForecastAI } from './gemini.js';

// ── Variabilele AQ monitorizate ──────────────────────────────────
const AQ_VARS = ['pm25', 'pm10', 'no2', 'o3', 'so2', 'co'];

// Accent colors per poluant
const POLLUTANT_COLORS = {
  pm25: '#ff6b6b',
  pm10: '#ffa94d',
  no2:  '#ffd43b',
  o3:   '#69db7c',
  so2:  '#74c0fc',
  co:   '#b197fc',
};

// ── State ────────────────────────────────────────────────────────
let cubes = {};         // { pm25: cube, pm10: cube, ... }
let allDates = [];      // Dates[] din cubul pm25 (referință)
let dayRanges = [];     // [{ label, start, end }] — indexuri per zi
let currentDayIndex = 0;
let expandedCityId = null;  // ID-ul orașului expandat în tabel
let expandedCountyName = null; // Județul expandat
// countyGridMapping is now managed by countyGridCache (Map) defined later

// Sort state: { key: 'name'|'pm25'|...|'category', dir: 'asc'|'desc' } | null
let citySort = { key: 'category', dir: 'desc' };
let countySort = { key: 'category', dir: 'desc' };

// ── Boot ─────────────────────────────────────────────────────────
document.addEventListener('DOMContentLoaded', boot);

async function boot() {
  try {
    // 1. Încarcă metadatele store
    const meta = await loadStoreMetadata();
    if (meta?.run_date) {
      document.getElementById('run-date-display').textContent =
        `RUN ${meta.run_date}`;
    }

    // 2. Încarcă toate cuburile AQ în paralel
    const cubArr = await Promise.all(AQ_VARS.map(v => loadCube(v)));
    AQ_VARS.forEach((v, i) => { cubes[v] = cubArr[i]; });

    // 3. Construiește axul de timp
    allDates = timesToDates(cubes.pm25.times, cubes.pm25.timeAttrs);
    dayRanges = buildDayRanges(allDates);

    // 4. Setup UI
    setupDaySelector();
    setupCitySearch();
    setupCountyFilter();
    setupSortableHeaders();
    updateAll();

    // 5. Ascunde loading
    document.getElementById('loading-overlay').classList.add('hidden');

  } catch (err) {
    console.error('Eroare la încărcarea datelor:', err);
    document.querySelector('.analysis-loading-content p').textContent =
      'Eroare la încărcarea datelor. Verificați conexiunea.';
  }
}

// ── Construiește ranges per zi din timestamps ────────────────────
function buildDayRanges(dates) {
  const ranges = [];
  const labels = ['Azi', 'Mâine', 'Poimâine', '+3 zile'];
  let currentDay = null;
  let rangeStart = 0;

  dates.forEach((d, i) => {
    const dayKey = d.toISOString().slice(0, 10);
    if (dayKey !== currentDay) {
      if (currentDay !== null) {
        const label = labels[ranges.length] || dayKey;
        ranges.push({ label, start: rangeStart, end: i - 1, date: currentDay });
      }
      currentDay = dayKey;
      rangeStart = i;
    }
  });
  // Ultimul range
  if (currentDay !== null) {
    const label = labels[ranges.length] || currentDay;
    ranges.push({ label, start: rangeStart, end: dates.length - 1, date: currentDay });
  }

  return ranges;
}

// ── Day Selector ─────────────────────────────────────────────────
function setupDaySelector() {
  document.getElementById('day-prev').addEventListener('click', () => {
    if (currentDayIndex > 0) {
      currentDayIndex--;
      updateAll();
    }
  });

  document.getElementById('day-next').addEventListener('click', () => {
    if (currentDayIndex < dayRanges.length - 1) {
      currentDayIndex++;
      updateAll();
    }
  });
}

function updateDaySelector() {
  const range = dayRanges[currentDayIndex];
  document.getElementById('day-label').textContent = range?.label || '—';
  document.getElementById('map-day-label').textContent = range?.label || '—';

  document.getElementById('day-prev').disabled = currentDayIndex <= 0;
  document.getElementById('day-next').disabled = currentDayIndex >= dayRanges.length - 1;
}

// ── Update All Sections ──────────────────────────────────────────
function updateAll() {
  updateDaySelector();
  updateForecastText();
  updateCitiesTable();
  updatePollutantCards();
  updateCountyStats();
  updateMiniMap();
  updateTimestamp();
}

// ── Timestamp ────────────────────────────────────────────────────
function updateTimestamp() {
  const now = new Date();
  const formatted = now.toLocaleDateString('ro-RO', {
    day: '2-digit', month: '2-digit', year: 'numeric',
    hour: '2-digit', minute: '2-digit',
  });
  document.getElementById('forecast-timestamp').innerHTML = `
    <svg viewBox="0 0 24 24" width="14" height="14" fill="none" stroke="currentColor" stroke-width="2">
      <circle cx="12" cy="12" r="10"/><polyline points="12,6 12,12 16,14"/>
    </svg>
    Emis la ${formatted}
  `;
}

// ── Forecast Text Generation ─────────────────────────────────────
function updateForecastText() {
  // Calculează medii per zi pentru fiecare poluant principal
  const dayStats = computeDayStats(currentDayIndex);

  // Generează text template (instant) pentru ziua curentă
  const todayRange = dayRanges[currentDayIndex];
  document.querySelector('#forecast-today .forecast-day-label').textContent =
    todayRange?.label || 'Azi';
  const todayText = generateForecastParagraph(dayStats, currentDayIndex);
  document.getElementById('forecast-today-text').textContent = todayText;

  // Fire AI generation in background for today
  generateAIForDay(dayStats, currentDayIndex, 'forecast-today-text');

  // Ziua +1
  if (currentDayIndex + 1 < dayRanges.length) {
    const nextRange = dayRanges[currentDayIndex + 1];
    document.querySelector('#forecast-tomorrow .forecast-day-label').textContent =
      nextRange?.label || 'Mâine';
    const tomorrowStats = computeDayStats(currentDayIndex + 1);
    document.getElementById('forecast-tomorrow-text').textContent =
      generateForecastParagraph(tomorrowStats, currentDayIndex + 1);
    document.getElementById('forecast-tomorrow').style.display = '';

    generateAIForDay(tomorrowStats, currentDayIndex + 1, 'forecast-tomorrow-text');
  } else {
    document.getElementById('forecast-tomorrow').style.display = 'none';
  }

  // Ziua +2 (Perspectivă)
  if (currentDayIndex + 2 < dayRanges.length) {
    const outRange = dayRanges[currentDayIndex + 2];
    document.querySelector('#forecast-outlook .forecast-day-label').textContent =
      outRange?.label || 'Perspectivă';
    const outlookStats = computeDayStats(currentDayIndex + 2);
    document.getElementById('forecast-outlook-text').textContent =
      generateForecastParagraph(outlookStats, currentDayIndex + 2);
    document.getElementById('forecast-outlook').style.display = '';

    generateAIForDay(outlookStats, currentDayIndex + 2, 'forecast-outlook-text');
  } else {
    document.getElementById('forecast-outlook').style.display = 'none';
  }
}

/**
 * Fire an async AI text generation for a specific day.
 * On success, replace the text element content with a smooth transition.
 * On failure, keep the template text (already set).
 */
async function generateAIForDay(stats, dayIdx, textElementId) {
  if (!stats) return;

  const range = dayRanges[dayIdx];
  if (!range) return;

  const dateStr = range.date
    ? new Date(range.date + 'T00:00:00Z').toLocaleDateString('ro-RO', {
        weekday: 'long', day: 'numeric', month: 'long'
      })
    : range.label;

  const el = document.getElementById(textElementId);
  if (!el) return;

  // Add a subtle loading indicator
  el.style.opacity = '0.7';

  try {
    const aiText = await generateForecastAI(stats, dayIdx, range.label, dateStr);
    if (aiText) {
      // Smooth transition
      el.style.transition = 'opacity 0.3s ease';
      el.style.opacity = '0';
      setTimeout(() => {
        el.textContent = aiText;
        el.style.opacity = '1';
      }, 300);
    } else {
      // Keep template, just restore opacity
      el.style.opacity = '1';
    }
  } catch {
    el.style.opacity = '1';
  }
}

function computeDayStats(dayIdx) {
  if (dayIdx >= dayRanges.length) return null;
  const range = dayRanges[dayIdx];
  const stats = {};

  for (const v of AQ_VARS) {
    const cube = cubes[v];
    const values = [];

    for (const city of CITIES) {
      for (let t = range.start; t <= range.end; t++) {
        const val = sampleCityValue(cube, city, t);
        if (val != null) values.push(val);
      }
    }

    const avg = values.length ? values.reduce((a, b) => a + b, 0) / values.length : 0;
    const max = values.length ? Math.max(...values) : 0;
    const min = values.length ? Math.min(...values) : 0;
    const cat = getAqiCategory(avg, v);
    stats[v] = { avg, max, min, cat, count: values.length };
  }

  return stats;
}

function generateForecastParagraph(stats, dayIdx) {
  if (!stats) return 'Date insuficiente.';

  const range = dayRanges[dayIdx];
  const pm25Cat = stats.pm25.cat;
  const o3Cat = stats.o3.cat;
  const pm10Cat = stats.pm10.cat;

  // Determinăm nivelul general pe baza PM2.5 (principalul indicator)
  const level = pm25Cat?.label?.toLowerCase() || 'bun';

  const dateStr = range.date
    ? new Date(range.date + 'T00:00:00Z').toLocaleDateString('ro-RO', { weekday: 'long', day: 'numeric', month: 'long' })
    : range.label;

  let text = '';

  // Construim textul natural
  if (level === 'bun') {
    text = `Calitatea aerului pe teritoriul României este predominant bună. ` +
           `Nivelurile de PM2.5 se mențin la o medie de ${stats.pm25.avg.toFixed(1)} µg/m³, ` +
           `iar ozonul (O₃) la ${stats.o3.avg.toFixed(0)} µg/m³. ` +
           `Activitățile în aer liber pot continua fără restricții.`;
  } else if (level === 'acceptabil') {
    text = `Calitate acceptabilă în cea mai mare parte a țării. ` +
           `PM2.5 la medie de ${stats.pm25.avg.toFixed(1)} µg/m³ cu maxime de ${stats.pm25.max.toFixed(1)} µg/m³. ` +
           `Persoanele cu afecțiuni respiratorii pot resimți un disconfort ușor.`;
  } else if (level === 'moderat') {
    text = `Atenție — niveluri moderate de poluare. ` +
           `PM2.5 atinge media de ${stats.pm25.avg.toFixed(1)} µg/m³, cu vârfuri la ${stats.pm25.max.toFixed(1)} µg/m³. ` +
           `Grupurile vulnerabile ar trebui să limiteze activitățile prelungite în aer liber.`;
  } else if (level === 'slab') {
    text = `Calitate slabă a aerului prognozată. ` +
           `PM2.5: ${stats.pm25.avg.toFixed(1)} µg/m³ medie, maxim ${stats.pm25.max.toFixed(1)} µg/m³. ` +
           `Se recomandă evitarea activităților în exterior, mai ales pentru copii și vârstnici.`;
  } else {
    text = `⚠️ Nivel ridicat de poluare! ` +
           `PM2.5 la ${stats.pm25.avg.toFixed(1)} µg/m³ cu vârfuri alarmante de ${stats.pm25.max.toFixed(1)} µg/m³. ` +
           `Evitați expunerea. Rămâneți în interior cu ferestrele închise.`;
  }

  // Adăugăm mențiunea de ozon dacă e semnificativ
  if (o3Cat && o3Cat.index >= 2) {
    text += ` De asemenea, nivelurile de ozon sunt ridicate (${stats.o3.avg.toFixed(0)} µg/m³).`;
  }

  return text;
}

// ── Sortable Tables ──────────────────────────────────────────────

/**
 * Sort rows in-place using the given sort config.
 * @param {Array} rows          - Array of row objects
 * @param {Object} sortState    - { key, dir } — current sort state
 * @param {Function} getName    - row => string (name field)
 * @param {Function} getValues  - row => { pm25: { avg }, ... }
 * @param {Function} getWorst   - row => number (worstIndex)
 */
function applySortToRows(rows, sortState, getName, getValues, getWorst) {
  if (!sortState) return;
  const { key, dir } = sortState;
  const mult = dir === 'asc' ? 1 : -1;

  rows.sort((a, b) => {
    let va, vb;

    if (key === 'name') {
      va = getName(a);
      vb = getName(b);
      return mult * va.localeCompare(vb, 'ro');
    }

    if (key === 'category') {
      va = getWorst(a);
      vb = getWorst(b);
    } else {
      // Pollutant key (pm25, pm10, no2, etc.)
      va = getValues(a)[key]?.avg ?? -Infinity;
      vb = getValues(b)[key]?.avg ?? -Infinity;
    }

    return mult * (va - vb);
  });
}

function setupSortableHeaders() {
  document.querySelectorAll('.sortable-th').forEach(th => {
    th.addEventListener('click', () => {
      const key = th.dataset.sortKey;
      const table = th.dataset.table; // 'cities' or 'counties'

      // Determine sort state reference
      const isCity = table === 'cities';
      let sortState = isCity ? citySort : countySort;

      // Toggle direction or set new key
      if (sortState && sortState.key === key) {
        sortState.dir = sortState.dir === 'asc' ? 'desc' : 'asc';
      } else {
        sortState = { key, dir: key === 'name' ? 'asc' : 'desc' };
      }

      // Save state
      if (isCity) {
        citySort = sortState;
      } else {
        countySort = sortState;
      }

      // Update header CSS classes
      updateSortIndicators(table, sortState);

      // Re-render table
      if (isCity) {
        updateCitiesTable();
      } else {
        updateCountyStats();
      }
    });
  });

  // Set initial indicators
  updateSortIndicators('cities', citySort);
  updateSortIndicators('counties', countySort);
}

function updateSortIndicators(tableName, sortState) {
  document.querySelectorAll(`.sortable-th[data-table="${tableName}"]`).forEach(th => {
    th.classList.remove('sort-active', 'sort-asc', 'sort-desc');
    if (sortState && th.dataset.sortKey === sortState.key) {
      th.classList.add('sort-active', sortState.dir === 'asc' ? 'sort-asc' : 'sort-desc');
    }
  });
}

// ── Cities Table ─────────────────────────────────────────────────
function updateCitiesTable() {
  const tbody = document.getElementById('cities-table-body');
  const range = dayRanges[currentDayIndex];
  if (!range) return;

  // Calculează media per oraș per variabilă pe ziua selectată
  const rows = CITIES.map(city => {
    const row = { city, values: {}, worstIndex: 0 };

    for (const v of AQ_VARS) {
      const cube = cubes[v];
      let sum = 0, count = 0;

      for (let t = range.start; t <= range.end; t++) {
        const val = sampleCityValue(cube, city, t);
        if (val != null) { sum += val; count++; }
      }

      const avg = count ? sum / count : null;
      const cat = getAqiCategory(avg, v);
      row.values[v] = { avg, cat };

      if (cat && cat.index > row.worstIndex) {
        row.worstIndex = cat.index;
        row.worstCat = cat;
      }
    }

    return row;
  });

  // Sortăm rândurile
  applySortToRows(rows, citySort, r => r.city.name, r => r.values, r => r.worstIndex);

  tbody.innerHTML = rows.map(row => {
    const cat = row.worstCat || { label: 'Bun', color: '#50c878' };
    const isExpanded = expandedCityId === row.city.id;
    const cells = AQ_VARS.map(v => {
      const d = row.values[v];
      if (!d || d.avg == null) return '<td>—</td>';
      const valCat = d.cat;
      const color = valCat?.color || '#94a3b8';
      return `<td style="color: ${color}; font-weight: 500">${d.avg.toFixed(1)}</td>`;
    }).join('');

    const dataRow = `<tr class="city-data-row${isExpanded ? ' expanded' : ''}" data-city-id="${row.city.id}">
      <td>${row.city.name}</td>
      ${cells}
      <td>
        <span class="city-category-badge" style="background: ${cat.color}20; color: ${cat.color}; border: 1px solid ${cat.color}40">
          <span class="city-category-dot" style="background: ${cat.color}"></span>
          ${cat.label}
        </span>
      </td>
    </tr>`;

    const detailRow = `<tr class="city-detail-row" data-detail-for="${row.city.id}">
      <td colspan="8">
        <div class="city-detail-panel${isExpanded ? ' open' : ''}" id="detail-${row.city.id}">
          <div class="city-detail-grid">
            ${AQ_VARS.map(v => `
              <div class="city-sparkline-card">
                <div class="city-sparkline-header">
                  <span class="city-sparkline-name" style="color: ${POLLUTANT_COLORS[v]}">${LABELS[v]}</span>
                  <span class="city-sparkline-value" style="color: ${row.values[v]?.cat?.color || '#94a3b8'}">
                    ${row.values[v]?.avg != null ? row.values[v].avg.toFixed(1) : '—'}
                    <span style="font-size:10px;color:var(--text-muted)">${UNITS[v]}</span>
                  </span>
                </div>
                <canvas class="city-sparkline-canvas" data-city-id="${row.city.id}" data-var="${v}"></canvas>
              </div>
            `).join('')}
          </div>
        </div>
      </td>
    </tr>`;

    return dataRow + detailRow;
  }).join('');

  // Attach click handlers
  tbody.querySelectorAll('.city-data-row').forEach(tr => {
    tr.addEventListener('click', () => {
      const cityId = tr.dataset.cityId;
      toggleCityDetail(cityId);
    });
  });

  // Draw sparklines for expanded city
  if (expandedCityId) {
    requestAnimationFrame(() => drawCitySparklines(expandedCityId));
  }
}

// ── Toggle city detail row ───────────────────────────────────────
function toggleCityDetail(cityId) {
  if (expandedCityId === cityId) {
    // Collapse
    expandedCityId = null;
    const panel = document.getElementById(`detail-${cityId}`);
    if (panel) panel.classList.remove('open');
    const dataRow = document.querySelector(`tr[data-city-id="${cityId}"]`);
    if (dataRow) dataRow.classList.remove('expanded');
  } else {
    // Collapse old
    if (expandedCityId) {
      const oldPanel = document.getElementById(`detail-${expandedCityId}`);
      if (oldPanel) oldPanel.classList.remove('open');
      const oldRow = document.querySelector(`tr[data-city-id="${expandedCityId}"]`);
      if (oldRow) oldRow.classList.remove('expanded');
    }
    // Expand new
    expandedCityId = cityId;
    const panel = document.getElementById(`detail-${cityId}`);
    if (panel) panel.classList.add('open');
    const dataRow = document.querySelector(`tr[data-city-id="${cityId}"]`);
    if (dataRow) dataRow.classList.add('expanded');
    requestAnimationFrame(() => drawCitySparklines(cityId));
  }
}

// ── Draw sparkline charts for a city ─────────────────────────────
function drawCitySparklines(cityId) {
  const city = CITIES.find(c => c.id === cityId);
  if (!city) return;

  const range = dayRanges[currentDayIndex];
  if (!range) return;

  const canvases = document.querySelectorAll(`canvas[data-city-id="${cityId}"]`);
  canvases.forEach(canvas => {
    const varName = canvas.dataset.var;
    const cube = cubes[varName];
    if (!cube) return;

    const color = POLLUTANT_COLORS[varName];

    // Extract hourly values for this city on selected day
    const latIdx = nearestIndex(cube.lats, city.lat);
    const lonIdx = nearestIndex(cube.lons, city.lon);
    const values = [];
    const hours = [];

    for (let t = range.start; t <= range.end; t++) {
      const [, ny, nx] = cube.shape;
      const v = cube.data[t * ny * nx + latIdx * nx + lonIdx];
      values.push(Number.isNaN(v) ? null : v);
      hours.push(allDates[t] ? allDates[t].getHours() : t - range.start);
    }

    // Canvas setup
    const rect = canvas.getBoundingClientRect();
    const dpr = window.devicePixelRatio || 1;
    const w = rect.width || 200;
    const h = rect.height || 48;
    canvas.width = w * dpr;
    canvas.height = h * dpr;
    const ctx = canvas.getContext('2d');
    ctx.scale(dpr, dpr);

    // Find data range
    const validVals = values.filter(v => v != null);
    if (validVals.length === 0) return;
    const vMin = Math.min(...validVals);
    const vMax = Math.max(...validVals);
    const vRange = vMax - vMin || 1;
    const padTop = 4;
    const padBottom = 14;
    const chartH = h - padTop - padBottom;

    // Draw filled area + line
    ctx.beginPath();
    let firstX = 0;
    let started = false;

    for (let i = 0; i < values.length; i++) {
      if (values[i] == null) continue;
      const x = (i / (values.length - 1)) * w;
      const y = padTop + chartH - ((values[i] - vMin) / vRange) * chartH;
      if (!started) {
        ctx.moveTo(x, y);
        firstX = x;
        started = true;
      } else {
        ctx.lineTo(x, y);
      }
    }

    // Fill
    const lastI = values.length - 1;
    const lastX = (lastI / (values.length - 1)) * w;
    ctx.lineTo(lastX, h - padBottom);
    ctx.lineTo(firstX, h - padBottom);
    ctx.closePath();
    const grad = ctx.createLinearGradient(0, 0, 0, h);
    grad.addColorStop(0, color + '30');
    grad.addColorStop(1, color + '05');
    ctx.fillStyle = grad;
    ctx.fill();

    // Line
    ctx.beginPath();
    started = false;
    for (let i = 0; i < values.length; i++) {
      if (values[i] == null) continue;
      const x = (i / (values.length - 1)) * w;
      const y = padTop + chartH - ((values[i] - vMin) / vRange) * chartH;
      if (!started) { ctx.moveTo(x, y); started = true; }
      else ctx.lineTo(x, y);
    }
    ctx.strokeStyle = color;
    ctx.lineWidth = 1.5;
    ctx.stroke();

    // Hour labels at bottom
    ctx.font = '9px Inter, sans-serif';
    ctx.fillStyle = 'rgba(255,255,255,0.35)';
    ctx.textAlign = 'center';
    const labelStep = Math.max(1, Math.floor(values.length / 6));
    for (let i = 0; i < values.length; i += labelStep) {
      const x = (i / (values.length - 1)) * w;
      ctx.fillText(`${hours[i]}h`, x, h - 2);
    }
  });
}

// ── City Search (Nominatim) ──────────────────────────────────────
let searchTimeout = null;

function setupCitySearch() {
  const input = document.getElementById('city-search-input');
  const results = document.getElementById('city-search-results');
  const spinner = document.getElementById('city-search-spinner');

  input.addEventListener('input', () => {
    const q = input.value.trim();
    if (q.length < 2) {
      results.classList.remove('open');
      return;
    }
    clearTimeout(searchTimeout);
    searchTimeout = setTimeout(() => searchLocality(q), 350);
  });

  // Close on click outside
  document.addEventListener('click', (e) => {
    if (!e.target.closest('#city-search-wrapper')) {
      results.classList.remove('open');
    }
  });

  input.addEventListener('keydown', (e) => {
    if (e.key === 'Escape') results.classList.remove('open');
  });
}

async function searchLocality(query) {
  const results = document.getElementById('city-search-results');
  const spinner = document.getElementById('city-search-spinner');
  spinner.classList.remove('hidden');

  try {
    const url = `https://nominatim.openstreetmap.org/search?format=json&countrycodes=ro&limit=8&q=${encodeURIComponent(query)}`;
    const res = await fetch(url, {
      headers: { 'Accept-Language': 'ro' }
    });
    const data = await res.json();

    if (data.length === 0) {
      results.innerHTML = '<li class="no-results">Nicio localitate găsită</li>';
    } else {
      results.innerHTML = data.map(place => {
        const parts = place.display_name.split(',');
        const name = parts[0].trim();
        const region = parts.slice(1, 3).join(',').trim();
        return `<li data-lat="${place.lat}" data-lon="${place.lon}" data-name="${name}">
          <span class="search-result-name">${name}</span>
          <span class="search-result-region">${region}</span>
        </li>`;
      }).join('');

      // Attach click handlers
      results.querySelectorAll('li:not(.no-results)').forEach(li => {
        li.addEventListener('click', () => {
          const name = li.dataset.name;
          const lat = parseFloat(li.dataset.lat);
          const lon = parseFloat(li.dataset.lon);
          addSearchedCity(name, lat, lon);
          results.classList.remove('open');
          document.getElementById('city-search-input').value = '';
        });
      });
    }

    results.classList.add('open');
  } catch (err) {
    console.error('Eroare căutare:', err);
    results.innerHTML = '<li class="no-results">Eroare la căutare</li>';
    results.classList.add('open');
  } finally {
    spinner.classList.add('hidden');
  }
}

function addSearchedCity(name, lat, lon) {
  const city = addCustomCity(name, lat, lon);
  expandedCityId = city.id;
  updateCitiesTable();
  // Scroll la noul oraș
  setTimeout(() => {
    const row = document.querySelector(`tr[data-city-id="${city.id}"]`);
    if (row) row.scrollIntoView({ behavior: 'smooth', block: 'center' });
  }, 100);
}

// ── Pollutant Cards ──────────────────────────────────────────────
function updatePollutantCards() {
  const container = document.getElementById('pollutant-cards');
  const range = dayRanges[currentDayIndex];
  if (!range) return;

  container.innerHTML = AQ_VARS.map(v => {
    const cube = cubes[v];
    const color = POLLUTANT_COLORS[v];
    const label = LABELS[v];
    const unit = UNITS[v];
    const thresholds = THRESHOLDS[v];
    const maxThreshold = thresholds[thresholds.length - 1];

    // Calculează statistici pe toate orașele
    const allValues = [];
    for (const city of CITIES) {
      for (let t = range.start; t <= range.end; t++) {
        const val = sampleCityValue(cube, city, t);
        if (val != null) allValues.push(val);
      }
    }

    const avg = allValues.length ? allValues.reduce((a, b) => a + b, 0) / allValues.length : 0;
    const max = allValues.length ? Math.max(...allValues) : 0;
    const min = allValues.length ? Math.min(...allValues) : 0;
    const cat = getAqiCategory(avg, v);
const barPct = Math.min(100, (avg / maxThreshold) * 100);

    return `
      <div class="pollutant-card" style="--card-accent: ${color}">
        <div class="pollutant-card-header">
          <span class="pollutant-name">${label}</span>
          <span class="pollutant-category" style="color: ${cat?.color || color}; border-color: ${cat?.color || color}40">
            ${cat?.label || '—'}
          </span>
        </div>
        <div class="pollutant-stats">
          <div class="stat-item">
            <div class="stat-label">Medie</div>
            <div class="stat-value">${avg.toFixed(1)} <span class="stat-unit">${unit}</span></div>
          </div>
          <div class="stat-item">
            <div class="stat-label">Minim</div>
            <div class="stat-value">${min.toFixed(1)} <span class="stat-unit">${unit}</span></div>
          </div>
          <div class="stat-item">
            <div class="stat-label">Maxim</div>
            <div class="stat-value">${max.toFixed(1)} <span class="stat-unit">${unit}</span></div>
          </div>
        </div>
        <div class="pollutant-bar-bg">
          <div class="pollutant-bar-fill" style="width: ${barPct}%; background: ${color}"></div>
        </div>
      </div>
    `;
  }).join('');
}

// ── County Stats ─────────────────────────────────────────────────

function setupCountyFilter() {
  const input = document.getElementById('county-filter-input');
  if (!input) return;
  input.addEventListener('input', () => {
    const q = input.value.trim().toLowerCase();
    const tbody = document.getElementById('counties-table-body');
    if (!tbody) return;
    tbody.querySelectorAll('tr.county-data-row').forEach(tr => {
      const name = tr.dataset.countyName || '';
      const match = !q || name.toLowerCase().includes(q);
      tr.style.display = match ? '' : 'none';
      const detail = tr.nextElementSibling;
      if (detail && detail.classList.contains('county-detail-row')) {
        detail.style.display = match ? '' : 'none';
      }
    });
  });
}

async function updateCountyStats() {
  const refCube = cubes.pm25;
  if (!refCube) return;

  // Uses shared buildCountyGridMapping defined below
  const counties = await loadCountyBoundaries();
  const mapping = buildCountyGridMapping(counties, refCube.lats, refCube.lons);
  renderCountyTable(mapping);
}

function renderCountyTable(mapping) {
  const tbody = document.getElementById('counties-table-body');
  if (!tbody) return;
  const range = dayRanges[currentDayIndex];
  if (!range) return;

  const countyNames = [...mapping.keys()].sort((a, b) => a.localeCompare(b, 'ro'));

  if (countyNames.length === 0) {
    tbody.innerHTML = '<tr><td colspan="8" class="county-no-data">Se calculează harta județelor…</td></tr>';
    return;
  }

  // Compute per-county stats
  const countyRows = countyNames.map(name => {
    const cells = mapping.get(name) || [];
    const row = { name, values: {}, worstIndex: 0, worstCat: null };

    for (const v of AQ_VARS) {
      const cube = cubes[v];
      if (!cube) { row.values[v] = null; continue; }

      const [, ny, nx] = cube.shape;
      let sum = 0, count = 0, min = Infinity, max = -Infinity;

      for (let t = range.start; t <= range.end; t++) {
        for (const { j, i } of cells) {
          const val = cube.data[t * ny * nx + j * nx + i];
          if (!Number.isNaN(val) && val != null) {
            sum += val;
            count++;
            if (val < min) min = val;
            if (val > max) max = val;
          }
        }
      }

      const avg = count ? sum / count : null;
      const cat = getAqiCategory(avg, v);
      row.values[v] = { avg, min: min === Infinity ? null : min, max: max === -Infinity ? null : max, cat };

      if (cat && cat.index > row.worstIndex) {
        row.worstIndex = cat.index;
        row.worstCat = cat;
      }
    }

    return row;
  });

  // Sortăm după criteriul activ
  applySortToRows(countyRows, countySort, r => r.name, r => r.values, r => r.worstIndex);

  // Get global max per pollutant (for threshold bars)
  const globalMax = {};
  for (const v of AQ_VARS) {
    globalMax[v] = 0;
    for (const row of countyRows) {
      if (row.values[v]?.max > globalMax[v]) globalMax[v] = row.values[v].max;
    }
  }

  tbody.innerHTML = countyRows.map(row => {
    const cat = row.worstCat || { label: 'Bun', color: '#50c878' };
    const isExpanded = expandedCountyName === row.name;
    const cells = AQ_VARS.map(v => {
      const d = row.values[v];
      if (!d || d.avg == null) return '<td>—</td>';
      const color = d.cat?.color || '#94a3b8';
      return `<td style="color: ${color}; font-weight: 500">${d.avg.toFixed(1)}</td>`;
    }).join('');

    const dataRow = `<tr class="county-data-row${isExpanded ? ' expanded' : ''}" data-county-name="${row.name}">
      <td>${row.name}</td>
      ${cells}
      <td>
        <span class="city-category-badge" style="background: ${cat.color}20; color: ${cat.color}; border: 1px solid ${cat.color}40">
          <span class="city-category-dot" style="background: ${cat.color}"></span>
          ${cat.label}
        </span>
      </td>
    </tr>`;

    const detailRow = `<tr class="county-detail-row" data-detail-county="${row.name}">
      <td colspan="8">
        <div class="county-detail-panel${isExpanded ? ' open' : ''}" id="county-detail-${row.name.replace(/\s+/g,'-')}">
          <div class="county-detail-grid">
            ${AQ_VARS.map(v => {
              const d = row.values[v];
              const color = POLLUTANT_COLORS[v];
              const avg = d?.avg != null ? d.avg.toFixed(1) : '—';
              const min = d?.min != null ? d.min.toFixed(1) : '—';
              const max = d?.max != null ? d.max.toFixed(1) : '—';
              const catColor = d?.cat?.color || '#94a3b8';
              const barPct = d?.avg != null && globalMax[v] > 0 ? Math.min((d.avg / globalMax[v]) * 100, 100) : 0;
              return `
                <div class="county-sparkline-card">
                  <div class="county-sparkline-header">
                    <span class="county-sparkline-name" style="color: ${color}">${LABELS[v]}</span>
                    <span class="county-sparkline-value" style="color: ${catColor}">${avg}
                      <span style="font-size:9px;color:var(--text-muted)">${UNITS[v]}</span>
                    </span>
                    <span class="county-sparkline-range">min ${min} · max ${max}</span>
                  </div>
                  <div class="county-sparkline-bar-bg">
                    <div class="county-sparkline-bar-fill" style="width: ${barPct}%; background: ${color}"></div>
                  </div>
                </div>
              `;
            }).join('')}
          </div>
        </div>
      </td>
    </tr>`;

    return dataRow + detailRow;
  }).join('');

  // Attach click handlers
  tbody.querySelectorAll('.county-data-row').forEach(tr => {
    tr.addEventListener('click', () => {
      toggleCountyDetail(tr.dataset.countyName);
    });
  });

  // Re-apply filter
  const filterVal = document.getElementById('county-filter-input')?.value?.trim().toLowerCase();
  if (filterVal) {
    tbody.querySelectorAll('tr.county-data-row').forEach(tr => {
      const name = tr.dataset.countyName || '';
      const match = name.toLowerCase().includes(filterVal);
      tr.style.display = match ? '' : 'none';
      const detail = tr.nextElementSibling;
      if (detail?.classList.contains('county-detail-row')) {
        detail.style.display = match ? '' : 'none';
      }
    });
  }
}

function toggleCountyDetail(countyName) {
  const safeId = n => `county-detail-${n.replace(/\s+/g,'-')}`;

  if (expandedCountyName === countyName) {
    expandedCountyName = null;
    const panel = document.getElementById(safeId(countyName));
    if (panel) panel.classList.remove('open');
    const row = document.querySelector(`tr[data-county-name="${countyName}"]`);
    if (row) row.classList.remove('expanded');
  } else {
    if (expandedCountyName) {
      const old = document.getElementById(safeId(expandedCountyName));
      if (old) old.classList.remove('open');
      const oldRow = document.querySelector(`tr[data-county-name="${expandedCountyName}"]`);
      if (oldRow) oldRow.classList.remove('expanded');
    }
    expandedCountyName = countyName;
    const panel = document.getElementById(safeId(countyName));
    if (panel) panel.classList.add('open');
    const row = document.querySelector(`tr[data-county-name="${countyName}"]`);
    if (row) row.classList.add('expanded');
  }
}

// ── County boundaries cache ──────────────────────────────────────
let countyGeoJSON = null;
let countyGridCache = null;  // Map<countyName, [{j, i}]> grid cells per county

async function loadCountyBoundaries() {
  if (countyGeoJSON) return countyGeoJSON;
  try {
    const res = await fetch(`${import.meta.env.BASE_URL}data/romania_counties_wgs84.geojson`);
    countyGeoJSON = await res.json();
  } catch (e) {
    console.warn('Nu s-au putut încărca limitele de județ:', e);
    countyGeoJSON = { features: [] };
  }
  return countyGeoJSON;
}

// ── Point-in-polygon (ray casting) ──────────────────────────────
function pointInPolygon(lon, lat, ring) {
  let inside = false;
  for (let k = 0, n = ring.length, l = n - 1; k < n; l = k++) {
    const [xi, yi] = ring[k], [xj, yj] = ring[l];
    if (((yi > lat) !== (yj > lat)) &&
        (lon < (xj - xi) * (lat - yi) / (yj - yi) + xi)) {
      inside = !inside;
    }
  }
  return inside;
}

function pointInFeature(lon, lat, feature) {
  const geom = feature.geometry;
  if (geom.type === 'Polygon') {
    return pointInPolygon(lon, lat, geom.coordinates[0]);
  } else if (geom.type === 'MultiPolygon') {
    for (const poly of geom.coordinates) {
      if (pointInPolygon(lon, lat, poly[0])) return true;
    }
  }
  return false;
}

// ── Build grid→county mapping (cached) ──────────────────────────
function buildCountyGridMapping(counties, lats, lons) {
  if (countyGridCache) return countyGridCache;

  const ny = lats.length;
  const nx = lons.length;
  const mapping = new Map();

  for (const feat of counties.features) {
    mapping.set(feat.properties.name, []);
  }

  for (let j = 0; j < ny; j++) {
    const lat = Number(lats[j]);
    for (let i = 0; i < nx; i++) {
      const lon = Number(lons[i]);
      for (const feat of counties.features) {
        if (pointInFeature(lon, lat, feat)) {
          mapping.get(feat.properties.name).push({ j, i });
          break;
        }
      }
    }
  }

  countyGridCache = mapping;
  return mapping;
}

// ── Compute centroid of a polygon feature ───────────────────────
function featureCentroid(feature) {
  const geom = feature.geometry;
  const rings = geom.type === 'MultiPolygon'
    ? geom.coordinates.map(p => p[0])
    : [geom.coordinates[0]];

  let totalLon = 0, totalLat = 0, totalPts = 0;
  for (const ring of rings) {
    for (const [lon, lat] of ring) {
      totalLon += lon;
      totalLat += lat;
      totalPts++;
    }
  }
  return [totalLon / totalPts, totalLat / totalPts];
}

// ── Mini Map — EAQI Composite Choropleth ────────────────────────
// Uses the European Air Quality Index (EEA 2024) "worst-case" method:
// compute sub-index for each pollutant per county, take the worst.
const EAQI_POLLUTANTS = ['pm25', 'pm10', 'o3', 'no2', 'so2'];
const EAQI_SHORT = { pm25: 'PM2.5', pm10: 'PM10', o3: 'O₃', no2: 'NO₂', so2: 'SO₂' };

async function updateMiniMap() {
  const canvas = document.getElementById('mini-map-canvas');
  const range = dayRanges[currentDayIndex];
  if (!range) return;

  // We need all EAQI pollutant cubes
  const refCube = cubes.pm25;
  if (!refCube) return;
  const [nt, ny, nx] = refCube.shape;

  const latMin = Number(refCube.lats[0]);
  const latMax = Number(refCube.lats[ny - 1]);
  const lonMin = Number(refCube.lons[0]);
  const lonMax = Number(refCube.lons[nx - 1]);

  // Geographic aspect ratio
  const midLat = (latMin + latMax) / 2;
  const cosLat = Math.cos(midLat * Math.PI / 180);
  const geoWidth = (lonMax - lonMin) * cosLat;
  const geoHeight = latMax - latMin;
  const aspectRatio = geoWidth / geoHeight;

  // Size canvas to fill container, respecting aspect ratio
  const container = document.getElementById('mini-map-container');
  const containerW = container ? container.clientWidth : 520;
  const dpr = window.devicePixelRatio || 1;
  const cssW = Math.min(containerW - 16, 700);  // leave small padding
  const cssH = Math.round(cssW / aspectRatio);
  const dw = Math.round(cssW * dpr);
  const dh = Math.round(cssH * dpr);

  canvas.width = dw;
  canvas.height = dh;
  canvas.style.width = cssW + 'px';
  canvas.style.height = cssH + 'px';

  // ── Compute average grid per pollutant ──
  const avgGrids = {};
  for (const p of EAQI_POLLUTANTS) {
    const cube = cubes[p];
    if (!cube) continue;
    const grid = new Float32Array(ny * nx);
    const cnt = new Float32Array(ny * nx);
    for (let t = range.start; t <= range.end; t++) {
      for (let j = 0; j < ny * nx; j++) {
        const v = cube.data[t * ny * nx + j];
        if (!Number.isNaN(v)) {
          grid[j] += v;
          cnt[j]++;
        }
      }
    }
    for (let j = 0; j < ny * nx; j++) {
      grid[j] = cnt[j] > 0 ? grid[j] / cnt[j] : NaN;
    }
    avgGrids[p] = grid;
  }

  // ── Load counties and build mapping ──
  const counties = await loadCountyBoundaries();
  const gridMapping = buildCountyGridMapping(counties, refCube.lats, refCube.lons);

  // ── Compute EAQI per county ──
  const countyEAQI = new Map();
  for (const [name, cells] of gridMapping) {
    // Average each pollutant across county cells
    const pollutantAvgs = {};
    for (const p of EAQI_POLLUTANTS) {
      if (!avgGrids[p]) continue;
      let sum = 0, cnt = 0;
      for (const { j, i } of cells) {
        const v = avgGrids[p][j * nx + i];
        if (!Number.isNaN(v)) {
          sum += v;
          cnt++;
        }
      }
      pollutantAvgs[p] = cnt > 0 ? sum / cnt : null;
    }
    const eaqi = computeEAQI(pollutantAvgs);
    countyEAQI.set(name, { ...eaqi, values: pollutantAvgs });
  }

  // ── Draw on canvas ──
  const ctx = canvas.getContext('2d');
  ctx.clearRect(0, 0, dw, dh);

  // Background
  ctx.fillStyle = '#0a0e17';
  ctx.fillRect(0, 0, dw, dh);

  function geoToPixel(lon, lat) {
    const px = ((lon - lonMin) / (lonMax - lonMin)) * dw;
    const py = ((latMax - lat) / (latMax - latMin)) * dh;
    return [px, py];
  }

  // ── Fill each county polygon ──
  for (const feature of counties.features) {
    const name = feature.properties.name;
    const eaqi = countyEAQI.get(name);
    const [r, g, b] = eaqi ? eaqiLevelToColor(eaqi.level) : [30, 40, 60];
    const fillColor = eaqi ? `rgb(${r}, ${g}, ${b})` : 'rgba(30, 40, 60, 0.5)';

    const geom = feature.geometry;
    const coordSets = geom.type === 'MultiPolygon'
      ? geom.coordinates.map(p => p[0])
      : [geom.coordinates[0]];

    ctx.fillStyle = fillColor;
    ctx.beginPath();
    for (const ring of coordSets) {
      for (let k = 0; k < ring.length; k++) {
        const [lon, lat] = ring[k];
        const [px, py] = geoToPixel(lon, lat);
        if (k === 0) ctx.moveTo(px, py);
        else ctx.lineTo(px, py);
      }
      ctx.closePath();
    }
    ctx.fill();
  }

  // ── Draw county borders ──
  ctx.strokeStyle = 'rgba(255, 255, 255, 0.5)';
  ctx.lineWidth = 1 * dpr;
  ctx.lineJoin = 'round';

  for (const feature of counties.features) {
    const geom = feature.geometry;
    const coordSets = geom.type === 'MultiPolygon'
      ? geom.coordinates.map(p => p[0])
      : [geom.coordinates[0]];

    for (const ring of coordSets) {
      ctx.beginPath();
      for (let k = 0; k < ring.length; k++) {
        const [lon, lat] = ring[k];
        const [px, py] = geoToPixel(lon, lat);
        if (k === 0) ctx.moveTo(px, py);
        else ctx.lineTo(px, py);
      }
      ctx.closePath();
      ctx.stroke();
    }
  }

  // ── Draw county labels ──
  ctx.textAlign = 'center';
  ctx.textBaseline = 'middle';

  for (const feature of counties.features) {
    const name = feature.properties.name;
    const mnemonic = feature.properties.mnemonic || name.slice(0, 2).toUpperCase();
    const eaqi = countyEAQI.get(name);
    const [clon, clat] = featureCentroid(feature);
    const [px, py] = geoToPixel(clon, clat);

    if (!eaqi) continue;

    // Show: county code + EAQI level (1-based) + dominant pollutant
    const levelDisplay = eaqi.level + 1; // 1-based for user display
    const dominantShort = EAQI_SHORT[eaqi.dominantPollutant] || '';
    const labelText = `${mnemonic} ${levelDisplay}`;

    // Label background pill
    const fontSize = Math.round(9 * dpr);
    ctx.font = `bold ${fontSize}px Inter, sans-serif`;
    const textW = ctx.measureText(labelText).width;
    const bgPad = 3 * dpr;

    ctx.fillStyle = 'rgba(0, 0, 0, 0.5)';
    ctx.beginPath();
    ctx.roundRect(px - textW / 2 - bgPad, py - 7 * dpr, textW + bgPad * 2, 14 * dpr, 3 * dpr);
    ctx.fill();

    ctx.fillStyle = 'rgba(255, 255, 255, 0.92)';
    ctx.fillText(labelText, px, py);
  }
}

// ── Helpers ──────────────────────────────────────────────────────
